import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { Box, FormControl, InputLabel, MenuItem, Stack } from '@mui/material';
import { getMonthNameFromMonthIdx, periodFromSearchParams } from './period.helpers';
import { Period } from './types';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { ErrorBanner } from 'shared/ui/error-banner';
import { InputSize } from 'shared/layout/constants';

type Props = {
  selectedPeriod: Period;
  onSelect: (value: Period) => void;
  periods?: Period[];
  isLoading?: boolean;
  size?: InputSize;
  readOnly?: boolean;
  withManager?: boolean;
  withQueryParams?: boolean;
  withAllYearOption?: boolean;
};

export function PeriodPicker({ onSelect, selectedPeriod, periods, isLoading = false, size = 'small', readOnly = false, withManager, withQueryParams, withAllYearOption }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const updatePeriod = (period: Period) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('year', `${period.year}`);
        if (withAllYearOption && period.month === -1) {
          params.delete('month');
        } else {
          params.set('month', `${period.month}`);
        }
        return params;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (!withManager) return;

    const periodFromParams = periodFromSearchParams(searchParams);
    const isAvailablePeriod =
      periodFromParams && (periods ?? []).some((period) => period.year === periodFromParams.year && period.month === periodFromParams.month);

    if (periodFromParams && isAvailablePeriod) {
      if (periodFromParams.year !== selectedPeriod.year || periodFromParams.month !== selectedPeriod.month) {
        onSelect(periodFromParams);
      }
      return;
    }

    updatePeriod(selectedPeriod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withManager]);

  if (isLoading)
    return (
      <Box sx={{ width: 300, height: 40 }}>
        <LoadingBanner />
      </Box>
    );

  if (!periods) return <ErrorBanner errorMessage={'Нет доступных периодов'} />;

  const years = Array.from(new Set(periods.map((period) => period.year)));
  const months = Array.from(new Set(periods.filter((period) => period.year === selectedPeriod.year).map((period) => period.month)));
  const monthOptions = withAllYearOption ? [-1, ...months] : months;
  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    const selectedMonthIdx = Number(event?.target?.value);
    if (Number.isNaN(selectedMonthIdx)) return;
    const newPeriod = selectedMonthIdx === -1
      ? { year: selectedPeriod.year, month: -1 }
      : periods?.find((period) => period.year === selectedPeriod.year && period.month === selectedMonthIdx);
    if (!newPeriod) throw new Error('Selected period was not found in available periods');
    if (withQueryParams) {
      updatePeriod(newPeriod);
    }
    onSelect(newPeriod);
  };
  const handleYearChange = (event: SelectChangeEvent<number>) => {
    const newYear = Number(event?.target?.value);
    if (Number.isNaN(newYear)) return;
    const newPeriod = withAllYearOption
      ? { year: newYear, month: -1 }
      : periods?.filter((period) => period.year === newYear).sort((a, b) => a.month - b.month)[0]; //first available month from selected year

    if (!newPeriod) throw new Error('Selected period was not found in available periods');
    if (withQueryParams) {
      updatePeriod(newPeriod);
    }
    onSelect(newPeriod);
  };
  
  return (
    <Stack direction="row" spacing={1}>
      <FormControl size="small" margin="normal" sx={{ minWidth: 100 }}>
        <InputLabel id="year-picker">Год</InputLabel>
        <Select disabled={readOnly} labelId="year-picker" value={selectedPeriod.year} label="Год" onChange={handleYearChange} size={size} sx={{ backgroundColor: 'white' }}>
          {years.map((year) => (
            <MenuItem value={year} key={year}>
              {String(year)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth={true} size="small" margin="normal" sx={{ minWidth: 150 }}>
        <InputLabel id="month-picker">Месяц</InputLabel>
        <Select disabled={readOnly} labelId="month-picker" value={selectedPeriod.month} label="Месяц" onChange={handleMonthChange} size={size} sx={{ backgroundColor: 'white' }}>
          {monthOptions.map((monthIdx) => (
            <MenuItem value={monthIdx} key={monthIdx}>
              {monthIdx === -1 ? 'Весь год' : getMonthNameFromMonthIdx(monthIdx)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}
